/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext, CommonActions, FormActionPipelines, FormFlowState, getSalesTaxRateListItemId, GraphicalTakeOffMainConfig, graphicalTakeOffMainDialogRoute, HlpQuoteCloneDataDialogConfig, hlpQuoteCloneDataDialogRoute, HlpQuoteCloneDialogEntryPoint, HousePlanFinderDialogConfig, housePlanFinderDialogRoute, PbComponentItemFinderDialogConfig, pbComponentItemFinderDialogRoute, PbComponentItemFinderListItem, PbHousePlanFinderDialogConfig, pbHousePlanFinderDialogRoute, SalesTaxRateFinderDialogConfig, salesTaxRateFinderDialogRoute, SalesTaxRateListItem, } from '@gh/builder-shared';
import { HpeInitType, QuotationBase } from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow, AppRouter, FormStateSnapshot, getAllNewSelectedEntities, getAllSelectedEntities, getFormStateSnapshotValue, getSingleNewSelectedEntity, ImmutablePageState, mutate, newSelectionModel, PAGE_STATE, queryDirty, queryFormMode, setFormStateSnapshotValue, State, } from '@gh/common-shared';
import { DirtyType, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  Confirmation, DialogService, FormMode, isCreateFormMode, NotificationService, TextMessageDialogConfig, WaitingService, yesAnswer, yesNoConfirmation, } from '@gh/core-ui';

import { SelectionModel } from '@gh/core-data';
import { differentFieldsWith } from '@gh/core-util';
import { EMPTY$, of$ } from '@gh/rx';
import { map$, filter$, switchMap$ } from '@gh/rx/operators';

import { first, isNil, omit } from 'lodash';
import { Observable } from 'rxjs';

import {
  QuoteDetailsBusinessService,
  QuoteTopDialogConfig,
  quoteTopDialogRoute,
} from '../../../../leads';
import {
  HlpQuoteDetailsInitResponse,
  HlpQuoteDetailsMasterInfo,
  QuoteDetailsSaveResponse,
  QuoteHpFinderListItem,
  quoteSalesTaxRateFromFinder,
  quoteSalesTaxRateToFinder,
  QuoteVariation,
  quoteVariationsFromFinder,
  QuoteVariationsIdsGetResponse,
  useZeroPricesForHp,
} from '../../../resources/hlp-quote.model';
import {
  calculateTopDerivedInfo,
  HlpQuoteDetailsState,
  queryQuotationBase,
  queryQuoteBaseHousePlan,
  queryQuoteDerivedInfo,
  queryQuoteHasErrors,
  queryQuoteHousePlan,
  queryQuoteMasterInfo,
  queryQuoteMasterInfoShadow,
  queryQuotePriceBook,
  queryQuoteRecalculateModel,
  queryQuoteSettings,
  queryQuoteTopFormHasErrors,
  queryQuoteVariation,
  queryQuoteVariations,
  QuoteDetailsStateEventType,
  VariationsFilter,
} from './hlp-quote-details.state';
import { HlpDetailsBusinessService } from '../../../resources/hlp-details-business.service';

export type QuoteSaveFlowState = FormFlowState<HlpQuoteDetailsState> & { saveResponse?: QuoteDetailsSaveResponse };

export enum QuoteMasterInfoChange { AutoApproved = 1, Approved = 2, Rejected = 3 }

@Injectable()
export class HlpQuoteDetailsCommandService {
  constructor(private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private businessService: QuoteDetailsBusinessService,
    private hlpService: HlpDetailsBusinessService,
    @Inject(PAGE_STATE) private form: ImmutablePageState<HlpQuoteDetailsState>,
    private state: State<HlpQuoteDetailsState>,
    private userContext: BuilderUserContext,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private waitingService: WaitingService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private router: AppRouter,
  ) {

  }

  load(init: HlpQuoteDetailsInitResponse, mode: FormMode): void {
    const supportsProfit = this.businessService.isCurrentUserSupportsProfit;
    this.state.mutate({
      type:            QuoteDetailsStateEventType.Load,
      init,
      supportsProfit,
      isProfitVisible: false,
      mode,
    });
  }

  setMasterInfo(snapshot: FormStateSnapshot<HlpQuoteDetailsMasterInfo>): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetMasterInfoShadow, snapshot });

    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));
    const masterInfoShadow = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfoShadow));

    const modifiedField = first(differentFieldsWith(masterInfo, masterInfoShadow));
    switch (modifiedField) {
      case 'priceBook':
        this.tryMasterInfoChange(['base', 'baseHousePlan'], 'msg_quote_change_pb');
        break;
      case 'base':
        this.tryMasterInfoChange(['baseHousePlan'], 'msg_quote_change_base');
        break;
      case 'baseHousePlan':
        this.tryMasterInfoChange([], 'msg_quote_change_hp');
        break;
      default:
        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo });
        break;
    }
  }

  cloneVariations(callback: () => void): void {
    if (!this.ensureSaved()) {
      return;
    }

    this.dialogService.openWithInput(hlpQuoteCloneDataDialogRoute, <HlpQuoteCloneDataDialogConfig>{
      entryPoint:  HlpQuoteCloneDialogEntryPoint.CloneQuote,
      priceBookId: this.hlpService.prepearData().priceBookId,
    },                               {
      autoFocus: false,
    }).afterClosed().pipe(
      filter$(Boolean),
      switchMap$((items: PbComponentItemFinderListItem[]) => this.businessService.generateVariationIdsHlp(
          this.hlpService.hlpDetails.houseAndLandPackageId,
          items.filter((item) => item.isComponent).length,
          items.filter((item) => !item.isComponent).length,
      ).pipe(
        this.waitingService.operator<QuoteVariationsIdsGetResponse>(),
        map$((generatedIdSets) => quoteVariationsFromFinder(
            generatedIdSets,
            items.map((item) => ({
              ...item,
// @ts-expect-error Legacy typing suppression
              name: item.description,
            })),
          ),
        ),
      )),
    ).subscribe((variations: QuoteVariation[]) => {
      this.state.mutate({ type: QuoteDetailsStateEventType.VariationAddSet, variations });
      callback();
    },
                this.notificationService.processAll);
  }

  addVariations(hlpId: string, callback: () => void): void {
    if (!this.ensureEditMode()) {
      return;
    }

    const pb = this.state.project(queryQuotePriceBook)!;
    const baseHp = this.state.project(queryQuoteBaseHousePlan)!;
    const hp = this.state.project(queryQuoteHousePlan)!;
    const base = this.state.project(queryQuotationBase);
    this.dialogService.openWithInput(pbComponentItemFinderDialogRoute, <PbComponentItemFinderDialogConfig>{
      multiselection: true,
      priceBookId:    pb.id,
      priceBookName:  pb.name,
      housePlanId:    base === QuotationBase.HouseEstimate ? baseHp && baseHp.housePlanId : hp.housePlanId,
      housePlanName:  base === QuotationBase.HouseEstimate ? baseHp && baseHp.code : hp.code,
      isNumericSelector: true,
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities),
      switchMap$((items: PbComponentItemFinderListItem[]) => {
        const duplicatedItems: PbComponentItemFinderListItem[] = items.reduce((acc, item) => {
          const count = item.count || 1;
          const copies = Array(count).fill(null).map(() => ({ ...item }));
          return acc.concat(copies);
        }, [] as PbComponentItemFinderListItem[]);
        return this.businessService.generateVariationIdsHlp(
          hlpId,
          duplicatedItems.filter((item) => item.isComponent).length,
          duplicatedItems.filter((item) => !item.isComponent).length,
        ).pipe(
          this.waitingService.operator<QuoteVariationsIdsGetResponse>(),
          map$((generatedIdSets) => quoteVariationsFromFinder(
            generatedIdSets,
            duplicatedItems,
          )))
      }))
      .subscribe((variations: QuoteVariation[]) => {
        this.state.mutate({ type: QuoteDetailsStateEventType.VariationAddSet, variations });
        callback();
      });
  }

  hlpCloneQuote(variationId: ID, callback: () => void): void {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));
    if (variation) {
      this.businessService.generateVariationIdsHlp(
        this.hlpService.hlpDetails.houseAndLandPackageId,
        variation.isComponent ? 1 : 0,
        variation.isComponent ? 0 : 1
      ).pipe(
        this.waitingService.operator<QuoteVariationsIdsGetResponse>(),
        map$((generatedIdSets) => {
          const newId = variation.isComponent ? 
            generatedIdSets.componentIds[0] : 
            generatedIdSets.itemIds[0];

          const clonedVariation = {
            ...variation,
            quotationItemId: newId,
            variationId: `item:${newId}`,
            dirtyType: DirtyType.New,
            isExistInJob: false,
            entityState: void 0,
          };
          return [clonedVariation];
        })
      ).subscribe((variations: QuoteVariation[]) => {
        this.state.mutate({ 
          type: QuoteDetailsStateEventType.VariationAddSet, 
          variations 
        });
        callback();
      });
    }
  }

  moveVariationCreditDebit(variationId: ID): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.VariationMoveCreditDebit, variationId });
  }

  viewVariationMemo(variationId: ID): void {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));

    if (variation) {
      this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
        title:   this.translationService.get('lbl_memo'),
        text:    variation.memo,
        buttons: [{ value: 'ok', title: this.translationService.get('lbl_ok'), type: 'primary' }],
      });
    }
  }

  removeVariation(variationId: ID): Observable<void> {
    const variation = this.state.project((s) => queryQuoteVariation(s, variationId));
    if (isNil(variation)) {
      return EMPTY$;
    }

    return this.dialogService.confirmation(yesNoConfirmation('msg_quote_remove_item_component',
                                                      { code: variation.itemCode })).pipe(
      filter$(yesAnswer),
      map$(() => ({ type: QuoteDetailsStateEventType.VariationRemove, variationId })),
      mutate(this.state));
  }

  setVariationField(variationId: ID, name: string, value: any): void {
    const mode = this.state.project(queryFormMode);
    if (isCreateFormMode(mode)) {
      this.notificationService.error('msg_quote_create_process');
      return;
    }
    this.state.mutate({ type: QuoteDetailsStateEventType.VariationSetField, variationId, name, value });
  }

  resetFocusedCell(): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.ResetFocusedCell });
  }

  setMemo(memo: string): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetBottomField, name: 'memo', value: memo });
  }

  setMemoOnTop(isMemoOnTop: boolean): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetBottomField, name: 'isMemoOnTop', value: isMemoOnTop });
  }

  searchHousePlan(): void {
    // verify that can we change this quote
    this.confirmMasterInfoChange([], 'msg_quote_change_hp').pipe(
      filter$(Boolean),
      filter$((answer) => answer !== QuoteMasterInfoChange.Rejected),
      // show house plan finder
      switchMap$(() => {
        const priceBook = this.state.project(queryQuotePriceBook);
        const baseHousePlan = this.state.project(queryQuoteBaseHousePlan);

        switch (this.state.project(queryQuotationBase)) {
          case QuotationBase.QuickQuote:
            return this.dialogService.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
              priceBookId:    priceBook!.id,
              priceBookName:  priceBook!.name,
              filter:         { allowQq: true, isSalesEstimate: true },
              preselected:    baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
              multiselection: false,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity), map$(useZeroPricesForHp));
          case QuotationBase.HouseEstimate:
            return this.dialogService.openWithInput(pbHousePlanFinderDialogRoute, <PbHousePlanFinderDialogConfig>{
              priceBookId:    priceBook!.id,  
              priceBookName:  priceBook!.name,  
              filter:         { isSalesEstimate: true },
              preselected:    baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
              multiselection: false,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity));
          case QuotationBase.SalesEstimate:
            return this.dialogService.openWithInput(housePlanFinderDialogRoute, <HousePlanFinderDialogConfig>{
              disableShowQqSwitcher: true,
              disableShowSeSwitcher: true,
              filter:                { hideEmpty: true },
              preselected:           baseHousePlan ? [baseHousePlan.housePlanId] : void 0,
            }).afterClosed().pipe(filter$(Boolean), map$(getSingleNewSelectedEntity), map$(useZeroPricesForHp));
          default:
            return EMPTY$;
        }
      }),
    )
      .subscribe((baseHousePlan: QuoteHpFinderListItem) => {
        // set house plan into top form snapshot
        this.state.mutate({
          type:        QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot:    setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => (
              { ...currentShadow, baseHousePlan }
            ),
          ),
          derivedInfo: calculateTopDerivedInfo(
            this.state.project(queryQuoteDerivedInfo),
            baseHousePlan.salesPrice,
            baseHousePlan.salesPriceWithoutGst,
          ),
        });

        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo, resetVariations: true });
      });
  }

  openStr(): void {
    if (!this.ensureEditMode()) {
      return;
    }

    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));

    this.dialogService.openWithInput(salesTaxRateFinderDialogRoute, <SalesTaxRateFinderDialogConfig>{
      multiselection: true,
      selectionModel: newSelectionModel(
        (
          masterInfo.salesTaxRates || []
        ).map(quoteSalesTaxRateToFinder),
        getSalesTaxRateListItemId,
      ),
      unselectable:   true,
    })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllSelectedEntities))
      .subscribe((salesTaxRates: SalesTaxRateListItem[]) => {
        // set house plan into top form snapshot
        this.state.mutate({
          type:     QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot: setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => ({
              ...currentShadow,
              salesTaxRates: salesTaxRates.map(quoteSalesTaxRateFromFinder),
            })),
        });

        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo });
      });
  }

  openGto(isDisabledGto: boolean): void {
    const mode = this.state.project(queryFormMode);

    switch (mode) {
      case FormMode.CreateMode:
        this.notificationService.error('msg_quote_create_process');
        break;
      case FormMode.EditMode: {
        // In Edit mode, GTO is opened only after all current changes were saved.
        if (this.state.project(queryDirty) || this.hlpService.mainForm.dirty) {
          this.dialogService.confirmation(yesNoConfirmation('msg_continue_will_save_changes')).pipe(
            filter$(yesAnswer),
            switchMap$(() => {
              const flow = this.hlpService.onProcess();
              return flow.run({
                trigger: 'process',
                form:    this.hlpService.mainForm,
                value:   this.hlpService.mainForm.submitValue,
              })
                .toFinalState().pipe(
                  // we have to guarantee that GTO is opened only after successful save
// @ts-expect-error Legacy typing suppression
                  filter$((s) => !!s.success));
            })).subscribe(() => {
            this.showGto(isDisabledGto);
          });
        } else {
          this.showGto(isDisabledGto);
        }

        break;
      }
      default:
        // in View mode, we allow to open GTO only if there is at least one measured ToP
        if (this.state.project(queryQuoteSettings).isGtoTopExist) {
          this.showGto(isDisabledGto);
        } else {
          this.notificationService.warning('msg_no_gto_measurements');
        }
        break;
    }
  }

  openHpe(): void {
    if (!this.ensureSaved()) {
      return;
    }

    const housePlan = this.state.project(queryQuoteHousePlan);

    if (isNil(housePlan)) {
      return;
    }

    this.router.navigateToChild(['/estimating/house-plans', housePlan.housePlanId, 'estimate'], {
      parentTitle: {
                     [FormMode.CreateMode]: 'lbl_quote_create',
                     [FormMode.EditMode]:   'lbl_quote_edit',
                     [FormMode.ViewMode]:   'lbl_quote_view',
                   }[this.state.project(queryFormMode)],
      queryParams: {
        initType:    HpeInitType.Open,
        priceBookId: this.state.project(queryQuotePriceBook)!.id,
      },
    });
  }

  openTops(formMode: number): void {
    if (!this.ensureSaved()) {
      return;
    }

    const housePlan = this.state.project(queryQuoteHousePlan);
    const priceBook = this.state.project(queryQuotePriceBook);

    if (housePlan && priceBook) {

      this.dialogService.openWithInput(quoteTopDialogRoute, <QuoteTopDialogConfig>{
        housePlanId: housePlan.housePlanId,
        priceBookId: priceBook.id,
        formMode,
      }).afterClosed().pipe(
        filter$(Boolean),
        switchMap$(() => this.hlpService.get(this.hlpService.hlpDetails.houseAndLandPackageId).pipe(
          this.waitingService.operator())))
        .subscribe((data) => {
          this.hlpService.isShowQuoteTab = false;
          this.hlpService.hlpDetails = data;
          this.hlpService.mainForm.build(data);
          setTimeout(() => {
            this.hlpService.isShowQuoteTab = true;
          }, 300);
        });
    }
  }

  toggleProfitVisiblity(visible: boolean): void {
    this.state.mutate({
      type: QuoteDetailsStateEventType.SetProfitVisibility,
      visible,
    });
  }

  recalculateFlow(): ActionFlow<QuoteSaveFlowState> {
    return this.pipelines.recalculate({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryQuoteHasErrors),
        () => this.handleErrors()),
      execute:      this.commonActions.async((state: QuoteSaveFlowState) =>
        this.businessService.calculate(this.state.project(queryQuoteRecalculateModel)).pipe(
          map$((saveResponse) => (
            { ...state, saveResponse }
          )))),
      finish:       (state: QuoteSaveFlowState) => {
        const { saveResponse } = state;
        console.log('recalculateFlow', saveResponse); 
        if (saveResponse) {
          this.state.mutate({ type: QuoteDetailsStateEventType.MergeOnRecalculate, saveResponse });
        }
      },
    });
  }

  setVariationListFilter(filter: VariationsFilter): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.ChangeVariationListFilter, variationsFilter: filter });
  }

  setVariationSelection(selectionModel: SelectionModel): void {
    this.state.mutate({ type: QuoteDetailsStateEventType.SetVariationSelection, selectionModel });
  }

  private showGto(isDisabledGto: boolean): void {
    const hp = this.state.project(queryQuoteHousePlan);
    const masterInfo = getFormStateSnapshotValue(this.state.project(queryQuoteMasterInfo));

    if (hp) {
      this.dialogService.openWithInput(
        graphicalTakeOffMainDialogRoute,
        <GraphicalTakeOffMainConfig>{
          id:          hp.housePlanId,
          entryPoint:  'QQ',
          title:       `${ hp.code } ${ hp.name }`,
          quickQuote:  true,
          priceBookId: masterInfo.priceBook!.id,
          isDisabledGto,
        },
        {
          panelClass: 'cdk-overlay-large',
        }).afterClosed().pipe(
        switchMap$(() => this.hlpService.get(this.hlpService.hlpDetails.houseAndLandPackageId).pipe(
          this.waitingService.operator())))
        .subscribe(
          (data) => {
            this.hlpService.isShowQuoteTab = false;
            this.hlpService.hlpDetails = data;
            this.hlpService.mainForm.build(data);
            setTimeout(() => {
              this.hlpService.isShowQuoteTab = true;
            }, 300);

          },
          this.notificationService.processAll,
        );
    }
  }

  private handleErrors(): void {
    if (!this.state.project(queryQuoteTopFormHasErrors)) {
      this.notificationService.error('msg_he_incorrect_data_in_tables');
    }

    this.state.mutate({ type: QuoteDetailsStateEventType.NavigateToInvalid });
  }

  private confirmMasterInfoChange(dependentFields: string[], messageId: string): Observable<QuoteMasterInfoChange> {
    const masterInfoSnapshot = this.state.project(queryQuoteMasterInfo);
    const masterInfo = getFormStateSnapshotValue(masterInfoSnapshot);
    const hasVariations = this.state.project(queryQuoteVariations).length > 0;
    const hasDependentFieldsSet = dependentFields.some((field) => masterInfo[field]);

    if (hasVariations || hasDependentFieldsSet) {

      return this.dialogService.confirmation(yesNoConfirmation(messageId)).pipe(
        map$((answer) => ({
          [Confirmation.Yes]: QuoteMasterInfoChange.Approved,
          [Confirmation.No]: QuoteMasterInfoChange.Rejected,
        }[answer] || QuoteMasterInfoChange.Rejected)));
    } else {
      return of$(QuoteMasterInfoChange.AutoApproved);
    }
  }

  private tryMasterInfoChange(dependentFields: string[], messageId: string): void {
    const answer$ = this.confirmMasterInfoChange(dependentFields, messageId);

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.Approved))
      .subscribe(() => {
        // resets all dependent fields in shadow state of master info form
        // resets also prices for current house plan

        this.state.mutate({
          type:        QuoteDetailsStateEventType.SetMasterInfoShadow,
          snapshot:    setFormStateSnapshotValue(
            this.state.project(queryQuoteMasterInfoShadow),
            (currentShadow) => <HlpQuoteDetailsMasterInfo>omit(currentShadow, dependentFields)),
          derivedInfo: calculateTopDerivedInfo(this.state.project(queryQuoteDerivedInfo)),
        });
        // commits reset values
        this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo, resetVariations: true });
      });

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.Rejected))
      // resets to previous values
      .subscribe(() => this.state.mutate({
        type:     QuoteDetailsStateEventType.SetMasterInfoShadow,
        snapshot: this.state.project(queryQuoteMasterInfo),
      }));

    answer$.pipe(
      filter$((answer) => answer === QuoteMasterInfoChange.AutoApproved))
      // just commits current shadow form, i. e. this change does not affect quotation a lot
      .subscribe(() => this.state.mutate({ type: QuoteDetailsStateEventType.CommitMasterInfo }));
  }

  private ensureSaved(): boolean {
    const mode = this.state.project(queryFormMode);
    const dirty = this.state.project(queryDirty);

    if (isCreateFormMode(mode)) {
      this.notificationService.error('msg_quote_create_process');
      return false;
    } else if (dirty) {
      this.notificationService.error('msg_save_first');
      return false;
    }

    return true;
  }

  private ensureEditMode(): boolean {
    const mode = this.state.project(queryFormMode);

    if (isCreateFormMode(mode)) {
      this.notificationService.error('msg_quote_create_process');
      return false;
    }

    return true;
  }
}
